import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { linkedInApi, LinkedInStatus, LinkedInConnectionInfo } from '../services/api';
import { useToast } from '../sectepe-ui/index';
import { useConfirm } from '../components/ui';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { Icon } from '../components/icons';

/**
 * Liest die Rueckkehr-Parameter des LinkedIn-OAuth-Callbacks
 * (?linkedin_connected=true / ?linkedin_error=...), meldet sie als Toast und
 * raeumt die URL ueber den Router auf.
 */
export function useLinkedInCallbackParams(onConnected?: () => void) {
  const location = useLocation();
  const navigate = useNavigate();
  const { push } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get('linkedin_connected') === 'true';
    const error = params.get('linkedin_error');
    if (!connected && !error) return;
    if (connected) {
      push({ message: 'LinkedIn-Konto erfolgreich verbunden.', severity: 'success' });
      onConnected?.();
    }
    if (error) {
      push({ message: `LinkedIn-Fehler: ${error}`, severity: 'error', duration: 0 });
    }
    params.delete('linkedin_connected');
    params.delete('linkedin_error');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
    // onConnected absichtlich nicht als Abhaengigkeit: nur pro URL-Wechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, navigate, push]);
}

export interface LinkedInConnectionCardProps {
  variant?: 'full' | 'compact';
  /** Zusaetzliche Knoepfe je Verbindung (z. B. Metriken synchronisieren). */
  extraActions?: (connection: LinkedInConnectionInfo) => React.ReactNode;
  /** Wird nach Verbinden/Trennen aufgerufen (z. B. refresh des Prozessstatus). */
  onChanged?: () => void;
  /** Vorab geladener Status, sonst laedt die Karte selbst. */
  status?: LinkedInStatus | null;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Eine Karte fuer den LinkedIn-Kontostatus, ueberall gleich: Uebersicht,
 * Statistiken, Kalender und geplante Posts zeigen dieselbe Komponente statt
 * drei verschiedener Bastelloesungen.
 */
export const LinkedInConnectionCard: React.FC<LinkedInConnectionCardProps> = ({
  variant = 'full', extraActions, onChanged, status: presetStatus,
}) => {
  const [status, setStatus] = useState<LinkedInStatus | null>(presetStatus ?? null);
  const [loading, setLoading] = useState(!presetStatus);
  const [busy, setBusy] = useState(false);
  const { push } = useToast();
  const { confirm } = useConfirm();
  // Konto verbinden/trennen gehoert zu linkedin.freigeben (Backend prueft GET /auth und DELETE /connections).
  const { darf } = useBerechtigung();
  const darfVerbinden = darf('linkedin.freigeben');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const s = await linkedInApi.getStatus();
      setStatus(s ?? { configured: false, connections: [] });
    } catch {
      setStatus({ configured: false, connections: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (presetStatus) {
      setStatus(presetStatus);
      setLoading(false);
      return;
    }
    load();
  }, [presetStatus, load]);

  const connect = async () => {
    try {
      setBusy(true);
      const { authUrl } = await linkedInApi.initiateOAuth();
      window.location.href = authUrl;
    } catch (err: any) {
      push({ message: err?.response?.data?.error || 'Fehler beim Verbinden mit LinkedIn', severity: 'error' });
      setBusy(false);
    }
  };

  const disconnect = async (conn: LinkedInConnectionInfo) => {
    const ok = await confirm({
      title: 'LinkedIn-Verbindung trennen?',
      message: 'Automatisches Veröffentlichen und Metrik-Abgleich stehen danach nicht mehr zur Verfügung.',
      confirmLabel: 'Trennen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setBusy(true);
      await linkedInApi.disconnect(conn.id);
      push({ message: 'LinkedIn-Verbindung getrennt.', severity: 'success' });
      await load();
      onChanged?.();
    } catch (err: any) {
      push({ message: err?.response?.data?.error || 'Fehler beim Trennen der Verbindung', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const connections = status?.connections ?? [];
  const active = connections.some((c) => c.isActive);

  return (
    <section className={`card linkedin-card linkedin-card--${variant}`} aria-label="LinkedIn-Konto">
      <div className="linkedin-card__head">
        <span className="linkedin-card__icon" aria-hidden="true"><Icon name="linkedin" size={18} /></span>
        <div className="linkedin-card__title">
          <h2 className="card-title">LinkedIn-Konto</h2>
          {!loading && (
            active ? (
              <span className="badge badge-success">Verbunden</span>
            ) : status?.configured ? (
              <span className="badge badge-warning">Nicht verbunden</span>
            ) : (
              <span className="badge badge-neutral">Nicht konfiguriert</span>
            )
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-secondary">Status wird geladen…</p>
      ) : !status?.configured ? (
        <p className="text-sm text-secondary">
          Die LinkedIn-Anbindung ist auf dem Server nicht konfiguriert (LINKEDIN_CLIENT_ID / LINKEDIN_CLIENT_SECRET).
          Beiträge lassen sich trotzdem planen und manuell veröffentlichen.
        </p>
      ) : connections.length === 0 ? (
        <div className="linkedin-card__connect">
          {variant === 'full' && (
            <p className="text-sm text-secondary">
              Verbinde dein LinkedIn-Konto, um Beiträge direkt zu veröffentlichen und Kennzahlen abzurufen.
            </p>
          )}
          {darfVerbinden ? (
            <button type="button" className="btn btn-primary" onClick={connect} disabled={busy}>
              <Icon name="link" size={14} /> Mit LinkedIn verbinden
            </button>
          ) : (
            <p className="text-sm text-muted">LinkedIn-Konten verbindet, wer Beiträge freigeben darf (Marketing, Vertrieb, Inhaber:in).</p>
          )}
        </div>
      ) : (
        <ul className="linkedin-card__list">
          {connections.map((conn) => (
            <li key={conn.id} className="linkedin-card__conn">
              {conn.profilePicUrl ? (
                <img src={conn.profilePicUrl} alt="" className="linkedin-card__avatar" />
              ) : (
                <span className="linkedin-card__avatar linkedin-card__avatar--fallback" aria-hidden="true">
                  <Icon name="user" size={16} />
                </span>
              )}
              <div className="linkedin-card__conn-text">
                <strong>{conn.displayName || 'LinkedIn-Konto'}</strong>
                <span className="text-xs text-muted">
                  {conn.isActive ? 'Aktiv' : 'Token abgelaufen – bitte neu verbinden'} · seit {formatDate(conn.createdAt)}
                </span>
              </div>
              <div className="linkedin-card__actions">
                {extraActions?.(conn)}
                {!conn.isActive && darfVerbinden && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={connect} disabled={busy}>
                    Neu verbinden
                  </button>
                )}
                {darfVerbinden && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => disconnect(conn)} disabled={busy}>
                    Trennen
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default LinkedInConnectionCard;
